import { useEffect, useState, useRef } from "react";
import { LeftMenu } from "../LeftMenu";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useOrder } from "../../hooks/useOrder";
import { useAppConfig } from "../../hooks/useAppConfig";
import { ConfigType } from "../../contexts/AppConfigContext";
import { Loading } from "../Loading";
import { CiSearch } from "react-icons/ci";
import { CiFilter } from "react-icons/ci";
import "./index.css";
import { OrderContext, OrderType } from "../../contexts/OrderContext";
import { usePluginspace } from "../../hooks/usePluginspace";
import api from "../../services/api";
import { useFilters } from "../../hooks/useFilters";
import "animate.css";
import { useSeller } from "../../hooks/useSeller";
import { PiFunnel, PiList, PiMagnifyingGlass } from "react-icons/pi";
import { useProduct } from "../../hooks/useProduct";
import { useToast } from "../../hooks/useToast";

type HeaderProps = {
  extraSpacingLeftMenu?: boolean;
  background: string;
  logo: string;
  colorIcon: string;
  fontCart: string;
  backgroundCart: string;
  noSales: boolean;
};

export function Header({
  extraSpacingLeftMenu = false,
  background,
  logo,
  colorIcon,
  fontCart,
  backgroundCart,
  noSales,
}: HeaderProps) {
  const navigate = useNavigate();
  const { getTokenSeller } = useSeller();
  const { pluginspace_id, category_id } = useParams();
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const { searching, setSearching } = useFilters();
  const { getOrder } = useOrder();
  const orderContext = useOrder();
  const [productName, setProductName] = useState("");
  const [order, setOrder] = useState<OrderType>();
  const { getAppConfig, getSellerAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loadingLogo, setLoadingLogo] = useState(true);
  const { getToken, pluginspace } = usePluginspace();
  const { pluginspaceData } = usePluginspace();
  const [isSeller, setIsSeller] = useState(false);
  const [searchParams] = useSearchParams();
  const isSellerRef = useRef<boolean>(false);
  const { getFilterProductOptions, saveFilterProductOptions } = useProduct();
  const toast = useToast();
  async function loadOrder() {
    setLoadingLogo(true);
    setConfig(
      isSellerRef.current ? await getSellerAppConfig() : await getAppConfig()
    );
    setLoadingLogo(false);
    const newOrder = await getOrder(pluginspace_id ? pluginspace_id : "");
    setOrder(newOrder);
  }

  useEffect(() => {
    setSeller();
    loadOrder();
  }, []);

  useEffect(() => {
    if (!searchParams.get("name")) {
      setProductName("");
    }
  }, [searchParams.get("name")]);

  async function toSeachPage(searchValue: string) {
    try {
      setSearching(false);
      if (!searchValue || searchValue.length < 3) {
        toast.setToastPosition("0rem");
        toast.openToast(
          "warning",
          "Favor inserir pelo menos 3 caracteres para realizar a busca"
        );
        return;
      }

      const filter = getFilterProductOptions();

      // Limpa todos os campos do filtro, exceto "prices"
      Object.keys(filter).forEach((key) => {
        if (key !== "prices") {
          // @ts-ignore
          filter[key] = Array.isArray(filter[key]) ? [] : "";
        }
      });

      filter.prices = {
        min: 0,
        max: 99999999,
      };

      await saveFilterProductOptions({
        ...filter,
        name: searchValue,
      });

      setProductName("");

      let currentPath = `/${pluginspace_id}/pdv/filtro`;
      navigate(`${currentPath}?refresh=${new Date().getTime()}`);
    } catch (error) {
      console.error("Error during search:", error);
    }
  }

  const search = (productName: string) => {
    setSearching(false);
    if (
      config &&
      (config.template === "food" || config.template === "retail")
    ) {
      if (config && config.content.pdv.showLayout === "l2") {
        navigate(
          `/${pluginspace_id}/products-new?name=${window.btoa(
            productName.trim()
          )}`
        );
      } else {
        navigate(
          `/${pluginspace_id}/products?name=${window.btoa(productName.trim())}`
        );
      }
    } else {
      toSeachPage(productName.trim());
    }
  };

  async function goToFoodMenu() {
    if (config && config.content.pdv.showLayout === "l2") {
      navigate(`/${pluginspace_id}/products-new`);
    } else {
      navigate(`/${pluginspace_id}/products`);
    }
  }

  const getLandmark = () => {
    const landmark = localStorage.getItem("landmark_synkar");
    if (landmark) {
      const landmarkItem = JSON.parse(window.atob(landmark));
      return landmarkItem.interval;
    } else {
      return null;
    }
  };

  const getCounterNumber = () => {
    return localStorage.getItem("counter_number");
  };

  const getCardNumber = () => {
    return localStorage.getItem("card_number");
  };

  const getTableNumber = () => {
    return localStorage.getItem("table_number");
  };

  const getInternalNumber = () => {
    return localStorage.getItem("internal_number");
  };

  function setSeller() {
    const tk = getTokenSeller();
    if (tk && tk.profile == "seller") {
      setIsSeller(true);
      isSellerRef.current = true;
    }
  }

  async function loadOrderByChange() {
    const newOrder = await getOrder(pluginspace_id ? pluginspace_id : "");
    setOrder(newOrder);
  }

  const navigateToCart = () => {
    try {
      // ✅ 1. Verificar comanda (localStorage é a fonte correta)
      const cardNumber = localStorage.getItem("card_number");

      // ✅ 2. Verificar se é template food/retail SEM mesa/comanda/balcão
      const hasTableNumber = getTableNumber();
      const hasCardNumber = getCardNumber(); // localStorage fallback
      const hasLandmark = getLandmark();
      const hasCounterNumber = getCounterNumber();
      const hasInternalNumber = getInternalNumber();

      const isFood = pluginspace?.type === "food";
      const hasAnyFoodMode =
        hasTableNumber ||
        hasCardNumber ||
        hasLandmark ||
        hasCounterNumber ||
        hasInternalNumber;
      const hasAppLogin = pluginspace?.appLogin;

      if (hasAnyFoodMode || cardNumber) {
        navigate(`/${pluginspace_id}/cart-card`);
        return;
      }

      // ✅ 3. Se for retail/ecommerce sem modo food e sem vendedor -> novo carrinho
      if (pluginspace && !isFood && !hasAnyFoodMode && !isSeller) {
        navigate(`/${pluginspace_id}/new-cart`);
        return;
      }


      // ✅ 4. Todos os outros casos -> carrinho padrão
      // (Vendedor, Food, ou sem login)
      navigate(`/${pluginspace_id}/cart`);
    } catch (error) {
      console.error("Erro ao navegar para carrinho:", error);
      // ✅ 5. Fallback em caso de erro
      try {
        navigate(`/${pluginspace_id}/cart`);
      } catch (fallbackError) {
        console.error("Erro crítico na navegação:", fallbackError);
        // Último recurso: reload com hash
        window.location.href = `/${pluginspace_id}/cart`;
      }
    }
  };

  useEffect(() => {
    loadOrderByChange();
  }, [orderContext]);
  return (
    <>
      <div className="all-header">
        <div className="header" style={{ backgroundColor: background }}>
          {loadingLogo ||
          (config && config?.content && config.content.useLogoMenu) ? (
            <>
              <div className="header-logo">
                {logo !== "" && (
                  <img
                    src={logo}
                    alt="Logo"
                    onClick={() =>
                      !loadingLogo
                        ? config?.template === "food" ||
                          config?.template === "retail"
                          ? goToFoodMenu()
                          : navigate(`/${pluginspace_id}/category`)
                        : {}
                    }
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="header-logo">
                <h2>{config?.name}</h2>
              </div>
            </>
          )}
          <div className="header-content">
            <div
              className="header-search"
              onClick={() => {
                if (!searching) setLeftMenuOpen(false);
                setSearching(!searching);
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 52 52"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M41.6317 21.312C41.6317 25.7925 40.1713 29.9329 37.7205 33.2932L50.0842 45.6645C51.3046 46.8846 51.3046 48.8648 50.0842 50.0849C48.8639 51.305 46.8833 51.305 45.6629 50.0849L33.2992 37.7137C29.9382 40.1739 25.797 41.6241 21.3156 41.6241C10.0923 41.6241 0.999512 32.5332 0.999512 21.312C0.999512 10.0909 10.0923 1 21.3156 1C32.539 1 41.6317 10.0909 41.6317 21.312ZM11.1526 29.2728C16.4742 36.1335 26.7272 36.1335 32.0488 29.2728C35.6799 24.6024 35.6799 17.9017 32.0488 13.2212C26.7272 6.36054 16.4742 6.36054 11.1526 13.2212C7.52149 17.8917 7.52149 24.5924 11.1526 29.2728Z"
                  stroke={colorIcon}
                  stroke-miterlimit="10"
                />
              </svg>
            </div>

            <div className="other-content-header">
              {/*
              <div className="header-filter">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 53 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => {
                    navigate(`/${pluginspace_id}/filter`);
                  }}
                >
                  <path
                    d="M2.36814 3.56015C3.00818 2.00006 4.37828 1 5.89838 1H48.0912C49.6013 1 50.9715 1.99006 51.6215 3.56015C52.2715 5.13024 52.0715 6.96034 51.1115 8.30041L33.2502 33.2418V47.4326C33.2502 48.7827 32.5902 50.0227 31.5201 50.6228C30.4501 51.2228 29.1999 51.1028 28.2499 50.2928L21.9995 44.9325C21.2094 44.2624 20.7494 43.2024 20.7494 42.0723V33.2418L2.87815 8.28041C1.92809 6.95034 1.72807 5.10023 2.37812 3.55015L2.36814 3.56015Z"
                    stroke={colorIcon}
                    stroke-miterlimit="10"
                  />
                </svg>
              </div>
              
              */}
              {noSales ? (
                <></>
              ) : (
                <div className="header-cart" onClick={() => navigateToCart()}>
                  {order &&
                  order.products.filter((product) => !product.table).length >
                    0 ? (
                    <div className="cart-with-items-top-header">
                      <svg
                        width="27"
                        height="25"
                        viewBox="0 0 52 52"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.999512 3.33954C0.999512 2.0398 1.93963 1 3.0998 1H7.08041C9.0107 1 10.7109 2.24975 11.5111 4.12938H47.4965C49.7968 4.12938 51.4771 6.56889 50.877 9.04839L47.2865 23.9154C46.5463 26.9848 44.0459 29.1244 41.2055 29.1244H20.4324L20.9025 31.9038C21.0925 33.0036 21.9627 33.8034 22.9728 33.8034H43.7359C44.8961 33.8034 45.8362 34.8432 45.8362 36.143C45.8362 37.4427 44.8961 38.4825 43.7359 38.4825H22.9728C19.9424 38.4825 17.342 36.083 16.7819 32.7737L7.78053 6.31894C7.72052 5.94901 7.43047 5.68906 7.09042 5.68906H3.10981C1.94964 5.68906 1.00949 4.64928 1.00949 3.34954L0.999512 3.33954ZM16.6918 46.3009C16.6918 43.7115 18.8122 41.6119 21.4326 41.6119C24.053 41.6119 26.1733 43.7115 26.1733 46.3009C26.1733 48.8904 24.053 50.99 21.4326 50.99C18.8122 50.99 16.6918 48.8904 16.6918 46.3009ZM44.8061 41.6219C47.4265 41.6219 49.5468 43.7215 49.5468 46.3109C49.5468 48.9004 47.4265 51 44.8061 51C42.1857 51 40.0654 48.9004 40.0654 46.3109C40.0654 43.7215 42.1857 41.6219 44.8061 41.6219Z"
                          stroke={colorIcon}
                          stroke-miterlimit="10"
                        />
                      </svg>

                      <div
                        className="circle-number"
                        style={{
                          backgroundColor: backgroundCart,
                        }}
                      >
                        <p style={{ color: fontCart }}>
                          {order &&
                            order.products.filter((product) => !product.table)
                              .length}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <svg
                      width="27"
                      height="25"
                      viewBox="0 0 52 52"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.999512 3.33954C0.999512 2.0398 1.93963 1 3.0998 1H7.08041C9.0107 1 10.7109 2.24975 11.5111 4.12938H47.4965C49.7968 4.12938 51.4771 6.56889 50.877 9.04839L47.2865 23.9154C46.5463 26.9848 44.0459 29.1244 41.2055 29.1244H20.4324L20.9025 31.9038C21.0925 33.0036 21.9627 33.8034 22.9728 33.8034H43.7359C44.8961 33.8034 45.8362 34.8432 45.8362 36.143C45.8362 37.4427 44.8961 38.4825 43.7359 38.4825H22.9728C19.9424 38.4825 17.342 36.083 16.7819 32.7737L7.78053 6.31894C7.72052 5.94901 7.43047 5.68906 7.09042 5.68906H3.10981C1.94964 5.68906 1.00949 4.64928 1.00949 3.34954L0.999512 3.33954ZM16.6918 46.3009C16.6918 43.7115 18.8122 41.6119 21.4326 41.6119C24.053 41.6119 26.1733 43.7115 26.1733 46.3009C26.1733 48.8904 24.053 50.99 21.4326 50.99C18.8122 50.99 16.6918 48.8904 16.6918 46.3009ZM44.8061 41.6219C47.4265 41.6219 49.5468 43.7215 49.5468 46.3109C49.5468 48.9004 47.4265 51 44.8061 51C42.1857 51 40.0654 48.9004 40.0654 46.3109C40.0654 43.7215 42.1857 41.6219 44.8061 41.6219Z"
                        stroke={colorIcon}
                        stroke-miterlimit="10"
                      />
                    </svg>
                  )}
                </div>
              )}
              <div className="header-menu">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => setLeftMenuOpen(!leftMenuOpen)}
                >
                  <path
                    d="M47.4696 43.9395H4.52997C2.58042 43.9395 1 45.5199 1 47.4694C1 49.419 2.58042 50.9994 4.52997 50.9994H47.4696C49.4191 50.9994 50.9996 49.419 50.9996 47.4694C50.9996 45.5199 49.4191 43.9395 47.4696 43.9395Z"
                    stroke={colorIcon}
                    stroke-miterlimit="10"
                  />
                  <path
                    d="M47.4696 22.208H4.52997C2.58042 22.208 1 23.7884 1 25.738C1 27.6875 2.58042 29.2679 4.52997 29.2679H47.4696C49.4191 29.2679 50.9996 27.6875 50.9996 25.738C50.9996 23.7884 49.4191 22.208 47.4696 22.208Z"
                    stroke={colorIcon}
                    stroke-miterlimit="10"
                  />
                  <path
                    d="M47.4696 1H4.52997C2.58042 1 1 2.58042 1 4.52997C1 6.47952 2.58042 8.05994 4.52997 8.05994H47.4696C49.4191 8.05994 50.9996 6.47952 50.9996 4.52997C50.9996 2.58042 49.4191 1 47.4696 1Z"
                    stroke={colorIcon}
                    stroke-miterlimit="10"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {searching && (
          <div
            style={{ backgroundColor: background }}
            className={`search-content-extended animate__animated ${
              searching ? "animate__slideInDown" : "animate__slideOutUp"
            }`}
          >
            <input
              type="text"
              className="header-search-input"
              value={productName}
              onChange={(event) => setProductName(event.target.value)}
              placeholder={"Pesquisar"}
              onKeyDown={(event) => {
                if (event.key === "Enter") search(productName);
              }}
              ref={(input) => {
                if (input && searching) {
                  input.focus();
                }
              }}
            />
            {productName && productName.length > 0 && (
              <div
                className="icon_remove"
                style={{
                  position: "absolute",
                  right: "18%",
                }}
              >
                <i
                  style={{ color: "#000000", height: "20px", width: "20px" }}
                  className="fa fa-times-circle"
                  onClick={() => setProductName("")}
                ></i>
              </div>
            )}
            <div
              onClick={() => search(productName)}
              className="search-arrow"
              style={{
                backgroundColor: colorIcon,
              }}
            >
              <i
                style={{ color: background }}
                className="fa fa-arrow-right"
              ></i>
            </div>
          </div>
        )}
        {leftMenuOpen && (
          <LeftMenu
            extraSpacingLeftMenu={extraSpacingLeftMenu}
            cbSelected={(selected) => setLeftMenuOpen(!selected)}
          />
        )}
      </div>
      {searching && (
        <div className="overlay-search" onClick={() => setSearching(false)} />
      )}
      {leftMenuOpen && (
        <div
          className="overlay-header"
          onClick={() => setLeftMenuOpen(false)}
        />
      )}
    </>
  );
}
